import { useCallback, useEffect, useRef, useState } from "react";

interface UseCarouselScrollOptions {
  /**
   * Delay in ms before attaching scroll listeners.
   * Useful when items animate in and you don't want scroll events
   * during animation to trigger nav button flashing.
   */
  listenerDelay?: number;
}

interface UseCarouselScrollReturn {
  scrollContainerRef: React.RefObject<HTMLDivElement | null>;
  canScrollLeft: boolean;
  canScrollRight: boolean;
  scroll: (direction: "left" | "right") => void;
  checkScroll: () => void;
  resetScroll: () => void;
}

export function useCarouselScroll({
  listenerDelay = 0,
}: UseCarouselScrollOptions = {}): UseCarouselScrollReturn {
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const checkScroll = useCallback(() => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } =
        scrollContainerRef.current;
      setCanScrollLeft(scrollLeft > 0);
      // -1 for subpixel rounding issues
      setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 1);
    }
  }, []);

  const scroll = useCallback((direction: "left" | "right") => {
    if (scrollContainerRef.current) {
      const scrollAmount = scrollContainerRef.current.clientWidth;
      scrollContainerRef.current.scrollBy({
        left: direction === "left" ? -scrollAmount : scrollAmount,
        behavior: "smooth",
      });
    }
  }, []);

  const resetScroll = useCallback(() => {
    setCanScrollLeft(false);
    setCanScrollRight(false);
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollLeft = 0;
    }
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      checkScroll();
      const container = scrollContainerRef.current;
      if (container) {
        container.addEventListener("scroll", checkScroll);
        window.addEventListener("resize", checkScroll);
      }
    }, listenerDelay);

    return () => {
      clearTimeout(timeoutId);
      const container = scrollContainerRef.current;
      if (container) {
        container.removeEventListener("scroll", checkScroll);
        window.removeEventListener("resize", checkScroll);
      }
    };
  }, [checkScroll, listenerDelay]);

  return {
    scrollContainerRef,
    canScrollLeft,
    canScrollRight,
    scroll,
    checkScroll,
    resetScroll,
  };
}
